import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { newsArticles } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const TABS = [
  { key: "all", label: "全部结果", icon: "ri-apps-2-line" },
  { key: "movie", label: "影视内容", icon: "ri-film-line" },
  { key: "news", label: "影视资讯", icon: "ri-newspaper-line" },
];

const HOT_KEYWORDS = ["色戒", "高清修复", "剧情", "纪录片", "悬疑", "演员"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [tab, setTab] = useState("all");

  usePageMeta({
    title: "色戒完整版无删减hd-站内搜索结果",
    description:
      "在色戒完整版无删减hd站内搜索影片标题、演员名称与影视资讯，结果按影视内容与资讯分类展示，支持关键词实时匹配。",
    keywords: "站内搜索,影片搜索,演员搜索,影视资讯搜索,色戒完整版无删减hd",
  });

  useEffect(() => {
    setKeyword(initialQuery);
    setTab("all");
  }, [initialQuery]);

  const trimmed = keyword.trim();

  const handleChange = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setSearchParams({ q: value.trim() }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const matchedMovies = useMemo(() => {
    if (!trimmed) return [];
    const needle = trimmed.toLowerCase();
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.subtitle,
        movie.director,
        movie.type,
        movie.region,
        movie.summary,
        movie.synopsis,
        ...movie.genres,
        ...movie.tags,
        ...movie.cast.map((person) => person.name),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [trimmed]);

  const matchedNews = useMemo(() => {
    if (!trimmed) return [];
    const needle = trimmed.toLowerCase();
    return newsArticles.filter((article) => {
      const haystack = [article.title, article.summary, article.category, ...article.paragraphs]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [trimmed]);

  const total = matchedMovies.length + matchedNews.length;

  const showMovies = (tab === "all" || tab === "movie") && matchedMovies.length > 0;
  const showNews = (tab === "all" || tab === "news") && matchedNews.length > 0;
  const noResult = trimmed.length > 0 && total === 0;

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
            搜索结果
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            输入影片标题、演员名称或资讯关键词，结果会在下方实时更新。搜索为前端纯文本匹配，仅覆盖站内演示数据。
          </p>

          <form
            onSubmit={(event) => event.preventDefault()}
            className="mt-6 flex flex-col gap-3 sm:flex-row"
          >
            <div className="relative min-w-0 flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => handleChange(event.target.value)}
                placeholder="例如：色戒、汤唯、高清修复"
                aria-label="搜索关键词"
                className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-11 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => handleChange("")}
                  aria-label="清空关键词"
                  className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-foreground-700"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-2-line" />
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门关键词</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => handleChange(word)}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
          <p className="text-xs text-foreground-600">
            {trimmed ? (
              <>
                关键词 <span className="font-semibold text-foreground-950">“{trimmed}”</span> 共找到{" "}
                <span className="font-semibold text-accent-600">{total}</span> 条结果
              </>
            ) : (
              <>尚未输入关键词，输入后即可实时查看匹配结果</>
            )}
          </p>
          {trimmed && (
            <div className="ml-auto flex rounded-md bg-background-100 p-1">
              {TABS.map((item) => {
                const count =
                  item.key === "movie"
                    ? matchedMovies.length
                    : item.key === "news"
                      ? matchedNews.length
                      : total;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] transition-colors ${
                      tab === item.key
                        ? "bg-background-50 font-medium text-foreground-950"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    <i className={item.icon} />
                    {item.label}
                    <span className="font-label text-[10px] text-foreground-400">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!trimmed && (
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">试试这些内容</h2>
            <p className="mt-2 text-sm text-foreground-600">
              你可以搜索影片名称、导演与演员，也可以搜索「高清修复」「谍战」「纪录片」这类题材关键词。
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {movies.slice(0, 4).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}

        {noResult && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="mt-5 font-heading text-base font-semibold text-foreground-900">
              没有找到与“{trimmed}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              检查一下关键词是否有错别字，或者试试更短的词。也可以直接浏览下方热门内容。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              {["色戒", "剧情", "纪录片", "悬疑"].map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => handleChange(word)}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  {word}
                </button>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3 text-left sm:grid-cols-4">
              {movies.slice(0, 4).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}

        {showMovies && (
          <section className="mt-2">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-base font-bold text-foreground-950">
                影视内容
                <span className="ml-2 font-label text-xs font-normal text-foreground-400">
                  {matchedMovies.length} 条
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {matchedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        )}

        {showNews && (
          <section className="mt-10">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-base font-bold text-foreground-950">
                影视资讯
                <span className="ml-2 font-label text-xs font-normal text-foreground-400">
                  {matchedNews.length} 条
                </span>
              </h2>
            </div>
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {matchedNews.map((article) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className="group flex flex-col gap-1 border-b border-background-200 px-4 py-4 transition-colors last:border-b-0 hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between md:px-5"
                >
                  <span className="flex min-w-0 items-start gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-article-line text-[13px]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block clamp-2 font-heading text-sm font-medium text-foreground-900 transition-colors group-hover:text-accent-600">
                        {article.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-500">
                        {article.category} · {article.summary}
                      </span>
                    </span>
                  </span>
                  <time className="shrink-0 pl-9 font-label text-[11px] text-foreground-400 sm:pl-4">
                    {article.date}
                  </time>
                </Link>
              ))}
            </div>
          </section>
        )}

        {trimmed && total > 0 && !showMovies && !showNews && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <p className="text-sm text-foreground-700">
              当前筛选分类下没有与“{trimmed}”匹配的内容
            </p>
            <button
              type="button"
              onClick={() => setTab("all")}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部 {total} 条结果
            </button>
          </div>
        )}

        {trimmed &&
          ((tab === "movie" && matchedMovies.length > 0) ||
            (tab === "news" && matchedNews.length > 0)) && (
          <p className="mt-6 text-center text-[11px] text-foreground-400">
            当前仅展示{tab === "movie" ? "影视内容" : "影视资讯"}结果，
            <button
              type="button"
              onClick={() => setTab("all")}
              className="ml-1 cursor-pointer text-accent-600 hover:text-accent-700"
            >
              查看全部 {total} 条结果
            </button>
          </p>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 border-t border-background-200 pt-6">
          <Link
            to="/category"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
          >
            <i className="ri-grid-line" />
            按分类浏览影片
          </Link>
          <Link
            to="/news"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
          >
            <i className="ri-newspaper-line" />
            浏览全部资讯
          </Link>
        </div>
      </div>
    </PageShell>
  );
}